import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Plus,
  CheckCircle2,
  Search,
  Upload,
  Download,
  X,
  Building2,
  Layers,
  ShoppingBag,
  ShoppingCart,
  User,
  Briefcase,
  UserPlus,
  Building,
} from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { AccountingSubNav } from '../../accounting/components/AccountingSubNav';
import { formatINR } from '@/shared/utils/currency';
import { api } from '@/shared/api/axios';
import { ROUTES } from '@/routes/paths';

import { Button } from '@/shared/components/ui/Button';
import { PageHeader } from '@/shared/components/ui/PageHeader';
export function PartiesListPage() {
  const queryClient = useQueryClient();
  const [parties, setParties] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // New Party Form State
  const [formData, setFormData] = useState({
    legalName: '',
    displayName: '',
    email: '',
    mobile: '',
    taxNumber: '',
    paymentTermsDays: 30,
    creditLimitMinor: 5000000, // 50,000 INR
    creditHoldPolicy: 'warn',
    currency: 'INR',
    roles: {
      customer: true,
      supplier: false,
      employee: false,
      subcontractor: false,
      lead: false,
      agency: false,
    },
  });

  // Import State
  const [importCsvText, setImportCsvText] = useState(
    'legalName,email,taxNumber,isCustomer,isSupplier,creditLimitMinor\nABC Trading Ltd,abc@trading.com,27AAAAA1234A1Z1,true,true,5000000\n,invalid@email.com,,true,false,0\nABC Trading Ltd,abc@trading.com,27AAAAA1234A1Z1,true,true,5000000'
  );
  const [importReport, setImportReport] = useState<any>(null);
  const [importing, setImporting] = useState(false);

  const fetchParties = () => {
    setLoading(true);
    let url = '/api/accounting/parties';
    const params = new URLSearchParams();
    if (roleFilter !== 'all') params.append('role', roleFilter);
    if (searchQuery) params.append('search', searchQuery);
    if (params.toString()) url += `?${params.toString()}`;

    api.get(url)
      .then((res) => {
        const data = res.data;
        if (data.ok || data.success) {
          setParties(Array.isArray(data.data) ? data.data : []);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchParties();
  }, [roleFilter, searchQuery]);

  const handleCreateParty = (e: React.FormEvent) => {
    e.preventDefault();
    const newPartyPayload = {
      legalName: formData.legalName,
      displayName: formData.displayName || formData.legalName,
      email: formData.email,
      mobile: formData.mobile,
      taxNumber: formData.taxNumber,
      creditLimitMinor: Number(formData.creditLimitMinor) || 0,
      creditHoldPolicy: formData.creditHoldPolicy,
      isCustomer: formData.roles.customer,
      isSupplier: formData.roles.supplier,
      isEmployee: formData.roles.employee,
      isSubcontractor: formData.roles.subcontractor,
      isLead: formData.roles.lead,
      isAgency: formData.roles.agency,
    };

    api.post('/api/accounting/parties', newPartyPayload)
      .then((res) => {
        const data = res.data;
        if (data.ok || data.success) {
          const createdParty = data.data;
          setToast(`Created party '${createdParty.legalName}' (${createdParty.contactId}) successfully!`);
          setRoleFilter('all');
          setSearchQuery('');
          setShowCreateModal(false);
          setParties((prev) => [createdParty, ...prev.filter((p) => p._id !== createdParty._id)]);
          queryClient.invalidateQueries({ queryKey: ['accounts', 'customers'] });
          queryClient.invalidateQueries({ queryKey: ['accounts'] });
          // Reset form
          setFormData({
            legalName: '',
            displayName: '',
            email: '',
            mobile: '',
            taxNumber: '',
            paymentTermsDays: 30,
            creditLimitMinor: 5000000,
            creditHoldPolicy: 'warn',
            currency: 'INR',
            roles: {
              customer: true,
              supplier: false,
              employee: false,
              subcontractor: false,
              lead: false,
              agency: false,
            },
          });
        }
      })
      .catch((err) => {
        const errMsg = err.response?.data?.error?.message
          || err.response?.data?.message
          || err.message
          || 'Failed to create party.';
        alert(`Creation Error: ${errMsg}`);
      });
  };

  const handleRunBatchImport = () => {
    setImporting(true);
    setImportReport(null);

    // Simple CSV parse
    const lines = importCsvText.trim().split('\n');
    if (lines.length <= 1) {
      alert('CSV format invalid. Please include header row and data rows.');
      setImporting(false);
      return;
    }

    const headers = lines[0].split(',').map((h) => h.trim());
    const rows = lines.slice(1).map((line) => {
      const vals = line.split(',').map((v) => v.trim());
      const rowObj: any = {};
      headers.forEach((h, idx) => {
        const val = vals[idx];
        if (h === 'isCustomer' || h === 'isSupplier') rowObj[h] = val === 'true';
        else if (h === 'creditLimitMinor') rowObj[h] = Number(val) || 0;
        else rowObj[h] = val || '';
      });
      return rowObj;
    });

    api.post('/api/accounting/parties/import', { rows })
      .then((res) => {
        const data = res.data;
        if (data.ok || data.success) {
          setImportReport(data.data);
          fetchParties();
        } else {
          alert(`Import Error: ${data.message}`);
        }
      })
      .finally(() => setImporting(false));
  };

  const handleExportCSV = () => {
    const headers = ['contactId', 'legalName', 'email', 'taxNumber', 'isCustomer', 'isSupplier', 'creditHoldPolicy'];
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...parties.map((p) => [p.contactId, `"${p.legalName}"`, p.email, p.taxNumber, p.isCustomer, p.isSupplier, p.creditHoldPolicy].join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Zynx_Parties_Export_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="text-slate-900 dark:text-slate-100">
      <AccountingSubNav />
      <div className="p-6 space-y-6">
        {toast && (
          <div className="p-4 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 rounded-xl text-emerald-800 dark:text-emerald-300 text-sm flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              {toast}
            </div>
            <button onClick={() => setToast(null)}><X className="w-4 h-4" /></button>
          </div>
        )}

        <PageHeader
          icon={Users}
          eyebrow="Accounts"
          title="Customers & Suppliers"
          description="Every party the business deals with — customers, suppliers, employees, subcontractors and agencies — in one ledger directory."
          actions={<>
            <Button variant="outline" onClick={() => setShowImportModal(true)}>
              <Upload className="h-4 w-4" /> Import
            </Button>
            <Button variant="outline" onClick={handleExportCSV}>
              <Download className="h-4 w-4" /> Export CSV
            </Button>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4" /> New Party
            </Button>
          </>}
        />

        {/* Filter Tabs & Search Box arranged vertically */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center overflow-x-auto whitespace-nowrap text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-900/50 p-3 rounded-xl border border-slate-200/80 dark:border-slate-800 shadow-sm">
            {[
              { id: 'all', label: 'All Parties' },
              { id: 'customer', label: 'Customers' },
              { id: 'supplier', label: 'Suppliers' },
              { id: 'employee', label: 'Employees' },
              { id: 'subcontractor', label: 'Subcontractors' },
              { id: 'lead', label: 'Leads' },
              { id: 'agency', label: 'Agencies' },
            ].map((tab, idx) => {
              let IconComponent = Users;
              if (tab.id === 'all') IconComponent = Layers;
              else if (tab.id === 'customer') IconComponent = ShoppingBag;
              else if (tab.id === 'supplier') IconComponent = ShoppingCart;
              else if (tab.id === 'employee') IconComponent = User;
              else if (tab.id === 'subcontractor') IconComponent = Briefcase;
              else if (tab.id === 'lead') IconComponent = UserPlus;
              else if (tab.id === 'agency') IconComponent = Building;

              const isActive = roleFilter === tab.id;

              return (
                <div key={tab.id} className="flex items-center shrink-0">
                  {idx > 0 && (
                    <span className="text-slate-300 dark:text-slate-700 mx-3 select-none text-[11px] font-light">&lt;</span>
                  )}
                  <button
                    onClick={() => setRoleFilter(tab.id)}
                    className={cn(
                      "inline-flex items-center gap-1.5 text-[13px] font-semibold transition-all duration-150 border-0 bg-transparent py-1 px-2.5 rounded-md hover:text-slate-900 dark:hover:text-slate-100",
                      isActive
                        ? 'text-blue-600 dark:text-blue-400 font-extrabold bg-blue-50/50 dark:bg-blue-950/20'
                        : 'text-muted-foreground'
                    )}
                  >
                    <IconComponent className={cn("w-4 h-4", isActive ? "text-blue-600 dark:text-blue-400" : "text-slate-400")} />
                    <span>{tab.label}</span>
                  </button>
                </div>
              );
            })}
          </div>

          <div className="relative max-w-md w-full">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search party by name, contact ID, GSTIN..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-lg text-xs w-full"
            />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="p-12 text-center text-slate-500">Loading parties directory...</div>
          ) : parties.length === 0 ? (
            <div className="p-12 text-center text-slate-400">No parties found matching criteria.</div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 font-semibold text-slate-900 dark:text-slate-100 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3">Contact ID</th>
                  <th className="px-4 py-3">Party / Legal Name</th>
                  <th className="px-4 py-3">Roles</th>
                  <th className="px-4 py-3">GSTIN / VAT</th>
                  <th className="px-4 py-3">Credit Indicator</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {parties.map((p) => {
                  const policy = p.creditHoldPolicy || 'warn';
                  const isBlocked = policy === 'block';
                  const isWarn = policy === 'warn';

                  return (
                    <tr key={p._id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                      <td className="px-4 py-3 font-mono font-bold text-indigo-600 dark:text-indigo-400">{p.contactId}</td>
                      <td className="px-4 py-3">
                        <div className="font-bold text-slate-900 dark:text-slate-100">{p.legalName}</div>
                        <div className="text-xs text-slate-500 font-mono">{p.email || p.mobile || 'No contact info'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {p.isCustomer && (
                            <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                              Customer
                            </span>
                          )}
                          {p.isSupplier && (
                            <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300">
                              Supplier
                            </span>
                          )}
                          {p.isEmployee && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-purple-100 text-purple-800">Employee</span>}
                          {p.isSubcontractor && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800">Subcontractor</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-400">
                        {p.taxNumber || '—'}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {isBlocked ? (
                          <span className="inline-flex items-center gap-1 font-bold text-rose-600 bg-rose-50 dark:bg-rose-950/60 px-2 py-0.5 rounded">
                            🔴 Blocked (Credit Hold)
                          </span>
                        ) : isWarn ? (
                          <span className="inline-flex items-center gap-1 font-bold text-amber-600 bg-amber-50 dark:bg-amber-950/60 px-2 py-0.5 rounded">
                            🟡 Warning: {formatINR((p.creditLimitMinor || 0) / 100)} Limit
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded">
                            🟢 Normal
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <NavLink
                          to={`${ROUTES.accountingV2Parties}/${p._id}`}
                          className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded text-xs font-semibold hover:underline"
                        >
                          Workspace
                        </NavLink>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create Party Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl max-w-4xl w-full p-8 shadow-2xl space-y-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
              <h2 className="text-xl font-bold flex items-center gap-2.5 text-slate-900 dark:text-white">
                <Building2 className="w-6 h-6 text-indigo-600 dark:text-indigo-400" /> Create Unified Party / Contact
              </h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={handleCreateParty} className="space-y-6 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="font-semibold block mb-1.5 text-slate-800 dark:text-slate-200">Legal Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. ABC Trading Ltd"
                    value={formData.legalName}
                    onChange={(e) => setFormData({ ...formData, legalName: e.target.value })}
                    className="w-full p-3 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="font-semibold block mb-1.5 text-slate-800 dark:text-slate-200">Display / Trade Name</label>
                  <input
                    type="text"
                    placeholder="e.g. ABC Trading"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    className="w-full p-3 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Roles Checkbox Section */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800/80 rounded-2xl space-y-3 border border-slate-200 dark:border-slate-700">
                <label className="font-bold text-slate-900 dark:text-slate-100 block text-sm">Unified Roles (Single Record for All Roles)</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { key: 'customer', label: 'Customer' },
                    { key: 'supplier', label: 'Supplier' },
                    { key: 'employee', label: 'Employee' },
                    { key: 'subcontractor', label: 'Subcontractor' },
                    { key: 'lead', label: 'Lead' },
                    { key: 'agency', label: 'Agency' },
                  ].map((r) => (
                    <label key={r.key} className="flex items-center gap-2.5 p-2.5 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 cursor-pointer font-medium text-sm text-slate-800 dark:text-slate-200 hover:border-indigo-500 transition">
                      <input
                        type="checkbox"
                        checked={(formData.roles as any)[r.key]}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            roles: { ...formData.roles, [r.key]: e.target.checked },
                          })
                        }
                        className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                      />
                      {r.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="font-semibold block mb-1.5 text-slate-800 dark:text-slate-200">Email</label>
                  <input
                    type="email"
                    placeholder="contact@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full p-3 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="font-semibold block mb-1.5 text-slate-800 dark:text-slate-200">Mobile / Phone</label>
                  <input
                    type="text"
                    placeholder="+91 9876543210"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    className="w-full p-3 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="font-semibold block mb-1.5 text-slate-800 dark:text-slate-200">GSTIN / VAT Number</label>
                  <input
                    type="text"
                    placeholder="27AAAAA1234A1Z1"
                    value={formData.taxNumber}
                    onChange={(e) => setFormData({ ...formData, taxNumber: e.target.value })}
                    className="w-full p-3 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-xl text-sm font-mono uppercase focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="font-semibold block mb-1.5 text-slate-800 dark:text-slate-200">Credit Limit (Paise)</label>
                  <input
                    type="number"
                    value={formData.creditLimitMinor}
                    onChange={(e) => setFormData({ ...formData, creditLimitMinor: Number(e.target.value) })}
                    className="w-full p-3 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-xl text-sm font-mono focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                  <p className="text-xs text-slate-500 mt-1">5,000,000 paise = ₹50,000 INR</p>
                </div>
                <div>
                  <label className="font-semibold block mb-1.5 text-slate-800 dark:text-slate-200">Credit Hold Policy</label>
                  <select
                    value={formData.creditHoldPolicy}
                    onChange={(e) => setFormData({ ...formData, creditHoldPolicy: e.target.value })}
                    className="w-full p-3 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  >
                    <option value="warn">🟡 Warn</option>
                    <option value="require_approval">🟠 Require Approval</option>
                    <option value="block">🔴 Block (Credit Hold)</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-slate-200 dark:border-slate-800 pt-5">
                <button type="button" onClick={() => setShowCreateModal(false)} className="px-5 py-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 font-semibold text-sm rounded-xl transition">
                  Cancel
                </button>
                <button type="submit" className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm rounded-xl shadow-md transition">
                  Save Unified Party
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Batch Import Modal with Row-level Error Validation */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-2xl w-full p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
              <h2 className="text-base font-bold flex items-center gap-2">
                <Upload className="w-5 h-5 text-indigo-600" /> Batch CSV Import with Row-Level Error Report
              </h2>
              <button onClick={() => setShowImportModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-3 text-xs">
              <label className="font-semibold block text-slate-700 dark:text-slate-300">Paste CSV Contents or Template Rows:</label>
              <textarea
                rows={5}
                value={importCsvText}
                onChange={(e) => setImportCsvText(e.target.value)}
                className="w-full p-3 font-mono text-[11px] border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-xl"
              />

              <button
                onClick={handleRunBatchImport}
                disabled={importing}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg shadow-sm w-full"
              >
                {importing ? 'Validating Batch...' : 'Validate & Import Batch'}
              </button>

              {importReport && (
                <div className="p-4 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-200 dark:border-slate-700 space-y-3">
                  <div className="flex justify-between font-bold text-sm">
                    <span className="text-emerald-600">Imported: {importReport.importedCount}</span>
                    <span className="text-rose-600">Failed Rows: {importReport.failedCount}</span>
                  </div>

                  {importReport.errors.length > 0 && (
                    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-slate-100 dark:bg-slate-800 font-bold border-b">
                          <tr>
                            <th className="p-2 w-16">Row #</th>
                            <th className="p-2 w-20">Status</th>
                            <th className="p-2">Validation Error</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-700 font-mono">
                          {importReport.errors.map((err: any, idx: number) => (
                            <tr key={idx} className="bg-rose-50/50 dark:bg-rose-950/30">
                              <td className="p-2 font-bold">{err.row}</td>
                              <td className="p-2 text-rose-600 font-bold">❌ Error</td>
                              <td className="p-2 text-rose-700 dark:text-rose-300">{err.error}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end border-t border-slate-200 dark:border-slate-800 pt-3">
              <button onClick={() => setShowImportModal(false)} className="px-4 py-2 bg-slate-100 dark:bg-slate-800 font-semibold text-xs rounded-lg">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PartiesListPage;
